import { memo, useEffect } from 'react';
import { BluLoader } from '@bludata/components';
import EmptyPage from '../utility_objects/EmptyPage';
import { useMsal } from '@azure/msal-react';
import { clearMsalCookies } from './loginMSALAuthProcess';
import clearMsalCacheKeys from './utils/clearMsalCacheKeys';
import { SESSION_TIMEOUT_KEY } from './useLoginMsal';
import { logAuthTrace } from './loginLogger';

/**
 * Rimuove gli item critici da localStorage senza fare localStorage.clear()
 * che distruggeva feature flag, preferenze utente e dati non-MSAL.
 */
const cleanupLocalStorage = () => {
  localStorage.removeItem('bluSession');
  localStorage.removeItem('bluSessionData');
  localStorage.removeItem(SESSION_TIMEOUT_KEY);
};

function LoginBlankLoaderPage() {
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const handleAuthError = async (error: unknown) => {
      console.error("Errore durante l'autenticazione con MSAL:", error);
      logAuthTrace('LoginBlankLoaderPage:authError', { error: String(error) });
      cleanupLocalStorage();
      clearMsalCookies();
      try {
        await clearMsalCacheKeys(instance);
      } catch {
        /* ignora */
      }
      const account = instance.getActiveAccount();
      await instance.logoutRedirect({
        account: account || undefined,
      });
    };

    const errorCallbackId = instance.addEventCallback((message) => {
      if (message.eventType === 'msal:loginFailure') {
        handleAuthError(message.error);
      } else if (message.eventType === 'msal:acquireTokenFailure') {
        console.error(
          "Errore durante l'acquisizione del token:",
          message.error,
        );
      }
    });

    const timer = setTimeout(async () => {
      try {
        console.error(
          '[MSAL] Timeout 45s raggiunto senza risposta, pulizia forzata.',
        );
        logAuthTrace('LoginBlankLoaderPage:timeout45s', {
          accountsLength: accounts.length,
        });
        // Pulisce i cookie MSAL accumulati (causa originale di traffico inflazionato)
        clearMsalCookies();
        cleanupLocalStorage();
        try {
          await clearMsalCacheKeys(instance);
        } catch {
          /* ignora */
        }

        if (accounts.length === 0) {
          const account = instance.getActiveAccount();
          await instance.logoutRedirect({
            account: account || undefined,
          });
        } else {
          console.warn('Utente autenticato, ricarico la pagina.');
          window.location.reload();
        }
      } catch (error) {
        clearMsalCookies();
        cleanupLocalStorage();
        try {
          await clearMsalCacheKeys(instance);
        } catch {
          /* ignora */
        }
        console.error("Errore durante l'esecuzione del timeout:", error);
        window.location.reload();
      }
    }, 45000);

    return () => {
      clearTimeout(timer);
      if (errorCallbackId !== undefined && errorCallbackId !== null) {
        instance.removeEventCallback(errorCallbackId);
      }
    };
  }, [instance, accounts]);

  return (
    <EmptyPage>
      <BluLoader open />
    </EmptyPage>
  );
}

export default memo(LoginBlankLoaderPage);
